import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, Bed, Home, ChevronDown } from 'lucide-react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { SETTINGS, settingOf, isSignature } from '../lib/roomSettings';

const STAY_FAQS = [
  {
    q: "What types of accommodation does Rudragarh offer?",
    a: "Eight unique stays across four settings: by the water (Cascade Cottages on the pond), into the stone (The Heritage Huts and The Catacomb Suite on the cave side), the heart of the resort (The Golden Adobe, The Grand Quad, and The Desert Riad Suite), and among the green (The Cocoon and The Blue Enclave near the greenhouse)."
  },
  {
    q: "Are the cave-side stays suitable for couples and for families?",
    a: "Yes — The Catacomb Suite has 2 double beds per unit and sleeps up to 4 guests with a private attached bathroom, making it ideal for both couples seeking a dramatic private experience and small families. The Heritage Huts also sleep 4 each."
  },
  {
    q: "Do all rooms have air conditioning?",
    a: "Yes, all accommodations at Rudragarh are fully air-conditioned. Each room also has a minibar. Room-specific amenities are listed on each room's detail page."
  },
  {
    q: "What are the check-in and check-out times?",
    a: "Check-in is from 11:00 AM and check-out is by 10:00 AM. Early check-in or late check-out may be available on request, subject to availability — please contact us in advance."
  },
];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const normalizeUrl = (url) =>
  url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

const settingFilters = [{ id: 'all', name: 'All Stays' }, ...SETTINGS];

const SORTS = {
  recommended: (a, b) => (isSignature(b) - isSignature(a)) || (b.price_per_night - a.price_per_night),
  'price-asc': (a, b) => a.price_per_night - b.price_per_night,
  'price-desc': (a, b) => b.price_per_night - a.price_per_night,
};

const TheStay = () => {
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState([]);
  const [inventory, setInventory] = useState({});
  const [openFaq, setOpenFaq] = useState(null);
  const [activeSetting, setActiveSetting] = useState(
    SETTINGS.some((s) => s.id === searchParams.get('setting')) ? searchParams.get('setting') : 'all'
  );
  const [minGuests, setMinGuests] = useState(0);
  const [sort, setSort] = useState('recommended');
  const [loading, setLoading] = useState(true);
  const [propertyTotals, setPropertyTotals] = useState(null);

  useEffect(() => {
    fetchRooms();
    fetchInventory();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`);
      setRooms(response.data);
    } catch (error) {
      console.error('Error fetching rooms:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInventory = async () => {
    try {
      const response = await axios.get(`${API}/rooms/inventory-summary`);
      const invMap = {};
      response.data.rooms?.forEach(r => {
        invMap[r.id] = r;
      });
      setInventory(invMap);
      setPropertyTotals(response.data.property_totals);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const getRoomInventory = (roomId) => inventory[roomId] || null;

  // Groups shown: all four settings, or just the selected one. Guest filter and
  // sort apply within each group; empty groups are hidden.
  const visibleGroups = (activeSetting === 'all' ? SETTINGS : SETTINGS.filter((s) => s.id === activeSetting))
    .map((s) => ({
      ...s,
      rooms: rooms
        .filter((r) => settingOf(r) === s.id)
        .filter((r) => !minGuests || r.max_guests >= minGuests)
        .sort(SORTS[sort]),
    }))
    .filter((g) => g.rooms.length > 0);

  // All rooms in page order, for the quick-jump strip
  const orderedRooms = SETTINGS.flatMap((s) =>
    rooms.filter((r) => settingOf(r) === s.id).sort(SORTS.recommended)
  );

  const jumpToRoom = (slug) => {
    setActiveSetting('all');
    setMinGuests(0);
    // wait a tick so the card exists after filters reset
    setTimeout(() => document.getElementById(`room-${slug}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  };

  const RoomCard = ({ room, index }) => (
    <motion.div
      id={`room-${room.slug}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="group scroll-mt-44"
    >
      <Link to={`/stay/${room.slug}`} data-testid={`room-${room.slug}`}>
        <div className="relative h-52 overflow-hidden">
          {room.images && room.images.length > 0 ? (
            <img
              src={normalizeUrl(room.images[0])}
              alt={room.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-void-subtle to-void flex flex-col items-center justify-center gap-2">
              <Bed size={28} className="text-gold-500/30" />
              <span className="text-foreground/20 text-xs uppercase tracking-widest">Photos Coming Soon</span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-void/60 via-transparent to-transparent" />

          {/* Signature Badge */}
          {isSignature(room) && (
            <div className="absolute top-3 right-3">
              <span className="px-2 py-0.5 bg-gold-500 text-black text-[11px] uppercase tracking-wider">
                Signature
              </span>
            </div>
          )}
        </div>

        <div className="p-4 bg-void-subtle border border-gold-500/10 border-t-0">
          <h3 className="font-heading text-lg group-hover:text-gold-500 transition-colors truncate">
            {room.name}
          </h3>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-foreground/50 text-xs">
            {getRoomInventory(room.id) ? (
              <>
                <span className="flex items-center gap-1">
                  <Home size={12} className="text-gold-500/70" />
                  {getRoomInventory(room.id).total_units} {getRoomInventory(room.id).total_units === 1 ? 'Unit' : 'Units'}
                </span>
                <span className="flex items-center gap-1">
                  <Bed size={12} className="text-gold-500/70" />
                  {getRoomInventory(room.id).total_beds} {getRoomInventory(room.id).total_beds === 1 ? 'Bed' : 'Beds'}
                </span>
                <span className="flex items-center gap-1">
                  <Users size={12} className="text-gold-500/70" />
                  Sleeps {getRoomInventory(room.id).total_capacity}
                </span>
              </>
            ) : (
              <span className="flex items-center gap-1">
                <Users size={12} className="text-gold-500/70" />
                Up to {room.max_guests} guests
              </span>
            )}
          </div>

          {/* Price */}
          <div className="flex items-end justify-between mt-3 pt-3 border-t border-gold-500/10">
            <div>
              {room.original_price > room.price_per_night && (
                <span className="text-foreground/40 line-through text-sm mr-1.5">
                  ₹{room.original_price.toLocaleString()}
                </span>
              )}
              <span className="text-gold-500 font-heading text-lg">
                ₹{room.price_per_night.toLocaleString()}
              </span>
              <span className="text-foreground/40 text-xs ml-1.5 whitespace-nowrap">/ night</span>
              {room.original_price > room.price_per_night && (
                <span className="text-[10px] uppercase tracking-wider bg-gold-500/20 text-gold-400 px-1.5 py-0.5 rounded ml-2 whitespace-nowrap">
                  {Math.round((1 - room.price_per_night / room.original_price) * 100)}% off
                </span>
              )}
            </div>
            <span className="text-gold-500 text-xs uppercase tracking-wider group-hover:translate-x-1 transition-transform">
              View →
            </span>
          </div>
        </div>
      </Link>
    </motion.div>
  );

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Our Stays | Rudragarh Resort, Jaisalmer</title>
        <meta name="description" content="Explore our unique stays at Rudragarh Resort — pond-side cottages, cave-side huts and suites, and garden rooms near Jaisalmer, Rajasthan." />
        <meta property="og:title" content="Our Stays | Rudragarh Resort" />
        <meta property="og:description" content="By the water, into the stone, or among the green — find where your desert story begins at Rudragarh." />
        <meta property="og:url" content="https://www.rudragarh.com/stay" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-hut-night.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Stays | Rudragarh Resort" />
        <meta name="twitter:description" content="By the water, into the stone, or among the green — find where your desert story begins at Rudragarh." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-hut-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/stay" />
      </Helmet>
      {/* Hero */}
      <section className="relative h-[40vh] min-h-[320px] flex items-center justify-center" data-testid="stay-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('/images/resort-hut-night.jpg')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="overline">Accommodations</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              Where Will You <span className="text-gold-500">Sleep</span>?
            </h1>
            <p className="text-foreground/70 max-w-xl mx-auto mb-6">
              Every stay belongs to a chapter of the land — by the water,
              into the stone, at the heart of the resort, or among the green.
            </p>

            {/* Property Capacity Stats */}
            {propertyTotals && (
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 mt-4">
                <div className="flex items-center gap-2 bg-void/50 backdrop-blur-sm px-4 py-2 border border-gold-500/20">
                  <Home size={18} className="text-gold-500" />
                  <span className="text-foreground/80">
                    <span className="text-gold-400 font-heading text-lg">{propertyTotals.total_units}</span> Units
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-void/50 backdrop-blur-sm px-4 py-2 border border-gold-500/20">
                  <Bed size={18} className="text-gold-500" />
                  <span className="text-foreground/80">
                    <span className="text-gold-400 font-heading text-lg">{propertyTotals.total_beds}</span> Beds
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-void/50 backdrop-blur-sm px-4 py-2 border border-gold-500/20">
                  <Users size={18} className="text-gold-500" />
                  <span className="text-foreground/80">
                    Sleeps <span className="text-gold-400 font-heading text-lg">{propertyTotals.total_capacity}</span>
                  </span>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* All stays at a glance — click to jump */}
      {!loading && orderedRooms.length > 0 && (
        <section className="bg-void-paper border-b border-gold-500/10" data-testid="rooms-overview-strip">
          <div className="max-w-7xl mx-auto container-padding py-5">
            <div className="flex gap-4 overflow-x-auto pb-1">
              {orderedRooms.map((room) => (
                <button
                  key={room.id}
                  onClick={() => jumpToRoom(room.slug)}
                  className="flex-shrink-0 w-32 text-left group/chip"
                  data-testid={`overview-${room.slug}`}
                >
                  <div className="h-20 w-32 overflow-hidden bg-void-subtle">
                    {room.images?.[0] ? (
                      <img
                        src={normalizeUrl(room.images[0])}
                        alt={room.name}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover/chip:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Bed size={18} className="text-gold-500/30" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs font-heading mt-1.5 truncate group-hover/chip:text-gold-500 transition-colors">
                    {room.name}
                  </p>
                  <p className="text-[11px] text-gold-500">₹{room.price_per_night.toLocaleString()}</p>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="sticky top-20 z-40 bg-void/95 backdrop-blur-md border-b border-gold-500/10 py-4">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
            {settingFilters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveSetting(filter.id)}
                className={`px-4 md:px-6 py-2 text-sm uppercase tracking-widest transition-all whitespace-nowrap ${
                  activeSetting === filter.id
                    ? 'bg-gold-500 text-black'
                    : 'text-foreground/60 hover:text-gold-500 border border-transparent hover:border-gold-500/30'
                }`}
                data-testid={`filter-${filter.id}`}
              >
                {filter.name}
              </button>
            ))}
            <select
              value={minGuests}
              onChange={(e) => setMinGuests(Number(e.target.value))}
              className="bg-void border border-gold-500/20 text-foreground/70 text-sm uppercase tracking-widest px-3 py-2 focus:outline-none focus:border-gold-500"
              data-testid="filter-guests"
            >
              <option value={0}>Any Guests</option>
              <option value={2}>Sleeps 2+</option>
              <option value={4}>Sleeps 4+</option>
              <option value={6}>Sleeps 6+</option>
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="bg-void border border-gold-500/20 text-foreground/70 text-sm uppercase tracking-widest px-3 py-2 focus:outline-none focus:border-gold-500"
              data-testid="filter-sort"
            >
              <option value="recommended">Recommended</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
            </select>
          </div>
        </div>
      </section>

      {/* Rooms — grouped by setting */}
      <section className="section-padding" data-testid="rooms-grid">
        <div className="max-w-7xl mx-auto container-padding">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-96 bg-void-subtle animate-pulse" />
              ))}
            </div>
          ) : visibleGroups.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-foreground/60">No stays match these filters.</p>
            </div>
          ) : (
            <div className="space-y-14">
              {visibleGroups.map((group) => (
                <div key={group.id} data-testid={`setting-group-${group.id}`}>
                  <div className="mb-6">
                    <span className="overline">{group.name}</span>
                    <p className="text-foreground/60 mt-1 text-sm max-w-2xl">{group.tagline}</p>
                    <div className="h-px bg-gold-500/20 mt-4" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {group.rooms.map((room, index) => (
                      <RoomCard key={room.id} room={room} index={index} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      {/* FAQ */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Questions</span>
            <h2 className="text-3xl font-heading font-medium">
              Accommodation <span className="text-gold-500">FAQs</span>
            </h2>
          </div>
          <div className="space-y-3">
            {STAY_FAQS.map((faq, i) => (
              <div key={i} className="bg-void-subtle border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={18}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-foreground/70 leading-relaxed border-t border-gold-500/10 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default TheStay;
